import React, { ChangeEvent, useState } from 'react';
import { FormControl, Select } from '@uhg-harmony/uitk-react';
import styled from 'styled-components';
import { getCombinedFontsForTheme } from 'builder/util/getCombinedFonts';
import {
  StyledClose,
  StyledPickerHeader,
  StyledPickerPopover,
  StyledPickerWrapper,
} from 'builder/components/GradientColorPicker/GradientColorPicker';
import {
  FontSizeInput,
  FontSizeInputWrapper,
  FontSizeUnitStyle,
  MainLabelStyles,
  handleNumberInput,
} from '../Typography/Typography';
import { ColorButtonWrapper } from '../Background/Background';
import { ColorButton } from 'builder/components/ColorButton/ColorButton';
import GradientColorPicker from 'builder/components/GradientColorPicker/GradientColorPicker';

export const FontSelect = styled(Select)`
  font-family: OptumSans, sans-serif;
  width: 9.375rem;
  border-radius: 0.375rem;
  margin-left: 0.75rem;
  & > select {
    font-family: OptumSans, sans-serif;
    line-height: 1.25rem;
  }
`;

export const FontSizeSelect = styled(Select)`
  font-family: OptumSans, sans-serif;
  width: 5.5rem;
  padding: 0 0.5rem 0 0.5rem;
  border-radius: 0.375rem;
  & > select {
    font-family: OptumSans, sans-serif;
    line-height: 1.25rem;
  }
`;

const FontDropdowns = styled.div`
  display: flex;
  align-items: baseline;
`;

const StyledWrapper = styled.div`
  padding-top: 0.5rem;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export const TextIconWrapper = styled.div`
  cursor: pointer;
  font-family: OptumSans, sans-serif;
  text-align: center;
  padding-right: 5px;
  border-radius: 7px;
  &:hover {
    background: #d9f6fa;
  }
`;

export const StyledCustomIcon = styled.span`
  height: 1em;
`;

export interface AccordionTypographyProps<T> {
  handleFontSize: (data: string) => void;
  handleFont: (data: string) => void;
  handleTextColor: (data: string) => void;
  widgetState: Widget<T>;
  pages: Array<Page>;
  site?: Site;
  updateSite?: (site: Site) => void;
  saveSite?: () => void;
  color: string;
  disabled?: boolean;
}

export const AccordionTypography: React.FC<AccordionTypographyProps<any>> = (
  accordionTypographyProps: AccordionTypographyProps<any>
) => {
  const {
    disabled,
    handleFontSize,
    handleFont,
    handleTextColor,
    widgetState,
    site,
    updateSite,
    saveSite,
  } = accordionTypographyProps;
  const { fontSize } = widgetState.config;

  const [pickerAnchorEl, setPickerAnchorEl] = useState<HTMLDivElement | null>(
    null
  );
  const pickerOpen = Boolean(pickerAnchorEl);
  const pickerId = pickerOpen
    ? 'simple-popover-accordion-typography'
    : undefined;

  const handlePickerClose = (e: React.MouseEvent<any>) => {
    e.stopPropagation();
    setPickerAnchorEl(null);
  };

  const handleFontChange = (e: ChangeEvent<HTMLSelectElement>) => {
    e.preventDefault();
    handleFont(e.target.value);
  };
  const handleFontSizeChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    handleFontSize(e.target.value);
  };

  const handleColorEditClick = (e: React.MouseEvent<any>) => {
    if (disabled) return;
    e.stopPropagation();
    e.preventDefault();
    setPickerAnchorEl(e.currentTarget);
  };
  const handlePickerClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
    e.preventDefault();
  };

  return (
    <>
      <FontDropdowns>
        <MainLabelStyles>Font</MainLabelStyles>
        <FormControl
          data-test-id="font-select-dropdown"
          id="controlled-select"
          disabled={disabled}
        >
          <FontSelect
            onChange={handleFontChange}
            value={widgetState.config.font}
          >
            {Object.keys(getCombinedFontsForTheme()).map(f => {
              return (
                <option value={f} key={f}>
                  {f}
                </option>
              );
            })}
          </FontSelect>
        </FormControl>
        <FontSizeInputWrapper>
          <FontSizeInput
            id="accordion-typography-font-size"
            value={fontSize}
            onChange={handleFontSizeChange}
            onKeyDown={handleNumberInput}
            data-test-id="accordion-typography-font-size"
            type="number"
            min={0}
          />
          <FontSizeUnitStyle>px</FontSizeUnitStyle>
        </FontSizeInputWrapper>
      </FontDropdowns>
      <StyledWrapper>
        <MainLabelStyles>Color</MainLabelStyles>
        <ColorButtonWrapper
          id="text-color"
          data-test-id="text-color-icon"
          onClick={handleColorEditClick}
        >
          <ColorButton
            color={widgetState.config.textColor}
            disabled={disabled}
          />
        </ColorButtonWrapper>
        <StyledPickerPopover
          data-test-id="widget-color-picker-popover"
          id={pickerId}
          open={pickerOpen}
          onClick={handlePickerClick}
          onClose={handlePickerClose}
          anchorEl={pickerAnchorEl}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
        >
          <StyledPickerWrapper>
            <StyledPickerHeader data-test-id="styled-picker-header">
              Color Selector
            </StyledPickerHeader>
            <StyledClose
              data-test-id="close-icon"
              onClick={handlePickerClose}
            />
          </StyledPickerWrapper>
          <GradientColorPicker
            value={widgetState.config.textColor}
            site={site}
            handleColorChange={handleTextColor}
            onChange={handleTextColor}
            updateSite={updateSite}
            saveSite={saveSite}
            anchorEl={pickerAnchorEl}
          />
        </StyledPickerPopover>
      </StyledWrapper>
    </>
  );
};
